import React, { useEffect, useState } from 'react';
import { X, Users, Loader2 } from 'lucide-react';
import { workspaceAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import type { WorkspaceMember, Agent } from '../types';

interface AgentAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  workspaceId: string;
  /**
   * Every agent id in the workspace — `str(agent._id)`, i.e. the `.id` field,
   * NOT the `agent_id` slug (`agent_ids` on a membership stores `_id`s).
   *
   * 🚨 Required to revoke correctly. A member scoped `all` has no stored id
   * list, so switching them to `selected` means materialising the list they
   * implicitly had and subtracting one — see `handleSave`.
   */
  allAgentIds: string[];
}

const AgentAccessModal: React.FC<AgentAccessModalProps> = ({ isOpen, onClose, agent, workspaceId, allAgentIds }) => {
  const { showToast } = useToast();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accessMap, setAccessMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) return;
    fetchMembers();
  }, [isOpen]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await workspaceAPI.getMembers(workspaceId);
      // ⚠️ Still the LITERAL `agents_manager` id, deliberately left alone by the
      // custom-roles ship (agent assignment must survive untouched, plan §7).
      // Known consequence: backend agent scoping applies to ANY non-owner,
      // non-admin member, so a member holding only a CUSTOM role that grants
      // `ai_agents` IS agent-scoped server-side but will not be listed here.
      // Widening this filter changes agent-scope behavior and needs its own
      // decision — do not do it as a side effect of a roles change.
      const agentsManagers: WorkspaceMember[] = (res.data as WorkspaceMember[]).filter(
        (m) => m.roles.includes('agents_manager') && !m.is_owner
      );
      setMembers(agentsManagers);

      const map: Record<string, boolean> = {};
      agentsManagers.forEach((m) => {
        if ((m.agent_scope || 'all') === 'selected') {
          map[m.user_id] = (m.agent_ids || []).includes(agent.id);
        } else {
          map[m.user_id] = true;
        }
      });
      setAccessMap(map);
    } catch {
      showToast('Failed to load team members', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (userId: string) => {
    setAccessMap((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates: Promise<any>[] = [];

      members.forEach((member) => {
        const hasAccess = accessMap[member.user_id] ?? true;
        const currentScope = member.agent_scope || 'all';
        const currentIds = member.agent_ids || [];

        if (hasAccess) {
          if (currentScope === 'selected' && !currentIds.includes(agent.id)) {
            updates.push(
              workspaceAPI.updateMemberAgentScope(workspaceId, member.user_id, {
                agent_scope: 'selected',
                agent_ids: [...currentIds, agent.id],
              })
            );
          }
          // scope === 'all': already has access, no change needed
        } else {
          if (currentScope === 'all') {
            // 🔴 Was `agent_ids: []` — which did not revoke ONE agent, it
            // revoked EVERY agent. A member scoped `all` implicitly has access
            // to the whole workspace; narrowing them to `selected` has to
            // materialise that implicit list and subtract only this agent,
            // otherwise un-ticking one box silently strips the other N-1.
            const expanded = allAgentIds.filter((id) => id !== agent.id);
            updates.push(
              workspaceAPI.updateMemberAgentScope(workspaceId, member.user_id, {
                agent_scope: 'selected',
                agent_ids: expanded,
              })
            );
          } else if (currentIds.includes(agent.id)) {
            updates.push(
              workspaceAPI.updateMemberAgentScope(workspaceId, member.user_id, {
                agent_scope: 'selected',
                agent_ids: currentIds.filter((id) => id !== agent.id),
              })
            );
          }
        }
      });

      await Promise.all(updates);
      showToast('Agent access updated', 'success');
      onClose();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to update access', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6 relative animate-fade-in flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Team Access</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{agent.config.agent_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors ml-2 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Control which team members can access this agent. Only members with the <span className="font-medium">Agents Manager</span> role are listed.
        </p>

        {/* Member list */}
        <div className="flex-1 overflow-y-auto space-y-2 mb-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              No Agents Manager members found.
            </p>
          ) : (
            members.map((member) => {
              const isAllScope = (member.agent_scope || 'all') === 'all';
              const hasAccess = accessMap[member.user_id] ?? true;
              const initials = (member.full_name || '')
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);

              return (
                <label
                  key={member.user_id}
                  className="flex items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {initials || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {member.full_name || member.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    {isAllScope && hasAccess && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">All Agents</span>
                    )}
                    <input
                      type="checkbox"
                      checked={hasAccess}
                      onChange={() => handleToggle(member.user_id)}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/50 accent-purple-600"
                    />
                  </div>
                </label>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex space-x-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-medium text-sm hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:scale-100"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentAccessModal;
